import {
  renderProviderName,
  SystemProblem,
  SystemProblemDetailsMeta,
} from '@vgrubs/components';

/**
 * What support should do about a problem, and whether they have to close it
 * themselves - in two sentences above the detail. The backend's description
 * says what happened; this says what is left. Wording follows the doc's type
 * table (when each closes) and its suggested actions.
 *
 * `duplicateStoreConnection` has its own, richer box (see SystemProblemModal).
 */
export type ProblemGuide = {
  /** What to do, in a sentence. */
  todo: string;
  /** When it goes away without anyone pressing Resolve. */
  closes: string;
};

const MANUAL_ONLY = 'It does not close by itself - mark it resolved when done.';

const provider = (meta: SystemProblemDetailsMeta | null) =>
  meta?.provider ? renderProviderName(meta.provider) : 'The provider';

export const problemGuide = (problem: SystemProblem): ProblemGuide | null => {
  const meta = (problem.meta ?? null) as SystemProblemDetailsMeta | null;
  const order = problem.orderNumber
    ? `order #${problem.orderNumber}`
    : 'the order';

  switch (problem.type) {
    case 'foodhubConnectionFailed':
      return {
        todo: `${provider(
          meta
        )}'s POS Hub connection failed. Open the brand's providers and connect it again.`,
        closes:
          'Closes by itself once the connection is no longer failed or is removed.',
      };
    case 'foodhubConnectionPending':
      return {
        todo: `${provider(
          meta
        )} has been pending on POS Hub for over 3 days with nothing changing. Check the connection on the brand's providers.`,
        closes: 'Closes by itself once it is no longer pending or is removed.',
      };
    case 'menuPublishFailed':
      return {
        todo: 'Publishing the menu failed. Fix what the error below names, then publish the menu again.',
        closes:
          'Closes by itself when the next publish of this menu to the same provider succeeds.',
      };
    case 'menuPublishStuck':
      return {
        todo: 'The menu publish has been in progress for over an hour, and the menu cannot be published again while it hangs. Check the menu sync.',
        closes: 'Closes by itself once the sync is no longer in progress.',
      };
    case 'vDriveAutoAssignFailed':
      return {
        todo: `No vDrive fleet took ${order}. Assign the delivery by hand from the order.`,
        closes:
          'Closes by itself once the order gets a delivery, or is completed or cancelled.',
      };
    case 'kitchenhubNotMainAccount':
      return {
        todo: `KitchenHub says the integration account is not the store's main account for ${provider(
          meta
        )}. Check the brand's connection.`,
        closes:
          'Closes by itself when the next nightly sync no longer reports it.',
      };
    case 'kitchenhubStoreNotFound':
      return {
        todo: "The brand's store no longer exists on KitchenHub. Check the brand's connections.",
        closes:
          'Closes by itself when the next nightly sync finds the store again.',
      };
    case 'kitchenhubProviderDisabled':
      return {
        todo: `KitchenHub reports ${provider(
          meta
        )} as disabled. Check the brand's connection.`,
        closes:
          'Closes by itself when the next check sees it enabled, or the provider is gone.',
      };
    case 'kitchenhubWorkingHoursFailed':
      return {
        todo: `KitchenHub refused the working hours sent for ${provider(
          meta
        )}, so the marketplace still runs on the old ones. Check the brand's working hours and save them again - saving sends them again.`,
        closes:
          'Closes by itself when the next send of the hours for this provider succeeds, or the provider is gone.',
      };
    case 'vDriveAutoRechargeFailed':
      return {
        todo: meta?.covered
          ? 'The card was declined on a vDrive auto-recharge; the vGrubs balance covered it this time. Get the card fixed on the location.'
          : 'The card was declined on a vDrive auto-recharge and nothing covered it - rides now go on overdraft. Get the card fixed on the location.',
        closes: 'Closes by itself with the next successful card charge.',
      };
    case 'posOrderRejected':
      return {
        todo: `The location's POS rejected ${order}. Check the order and sort it out with the restaurant.`,
        closes: MANUAL_ONLY,
      };
    case 'vDriveBrandMissingOrder':
      return {
        todo: `A virtual brand without vDrive got ${order}, which the restaurant has to deliver itself. Make sure it gets delivered.`,
        closes:
          'Closes by itself once the order gets a delivery, or is completed or cancelled.',
      };
    case 'tabletOffline':
      return {
        todo: `${order.charAt(0).toUpperCase()}${order.slice(
          1
        )} arrived while no tablet was connected. Call the restaurant.`,
        closes:
          'Closes by itself when a tablet reconnects, or the next order reaches a device.',
      };
    case 'providerOffline':
      return {
        todo: `${provider(
          meta
        )} is offline while the brand should be open, and no pause explains it. Check the tablet and the store on the marketplace.`,
        closes:
          'Closes by itself when the provider is back online, the hours end, or it is paused on purpose.',
      };
    case 'scheduledPauseFailed':
      return {
        todo: 'A pause set for later did not start, so the store is still taking orders. Pause it by hand and tell the restaurant.',
        closes: MANUAL_ONLY,
      };
    case 'printerError':
      return {
        todo:
          problem.severity === 'critical'
            ? `The receipt for ${order} did not print. Call the restaurant.`
            : "A tablet printer has been in error for over 5 minutes, or its tablet stopped reporting (Inactive in the location's printers). Call the restaurant; the location's printers show what each one reports.",
        closes:
          'Closes by itself when the tablet reports the printer ok or removes it, or after 7 days without a report.',
      };
    case 'saasNotEligible':
      return {
        todo: "The location pays a SaaS fee, but vGrubs brought it too little over the last 30 days to be SaaS eligible. Billing goes on as it is: decide whether to lower the fee or turn it off in the location's services.",
        closes:
          'Closes by itself at the next daily check once the location is eligible again, no longer pays SaaS or is deleted - lowering the fee to $0 does not close it right away.',
      };
    default:
      return null;
  }
};
